import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Fieldset } from '@digdir/designsystemet-react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon } from '@navikt/aksel-icons';

import { ErrorBox } from '../../../../../components/errorBox';
import { useRegistrationSteps } from '../../../hooks/useRegistrationSteps';
import { RegistrationCommonStep } from '../../../../../utils/navigation';
import { FormCard } from '../../omBoligen/form/components/styledComponents';
import { ButtonWrapper } from '../../solceller/form/components/styledComponents';
import { FormBackButton } from '../../../components/formBackButton';
import { UpdateRegistrationDTO } from '../../../../../services/types';

import { BygningsformCheckBox } from './components/bygningsformCheckBox';
import { CheckboxWrapper } from './components/styledComponents';
import { useValgfriDetaljeringSubmit } from './hooks/useValgfriDetaljeringSubmit';
import { DetaljeringForm } from './types';
import { hasUncheckedDetaljeringSteps } from './utils';
import { DetailingCheckbox } from './components/detailingCheckbox';
import { MightLoseDataWarning } from './components/mightLoseDataWarning';
import { useDetaljeringFormData } from './hooks/useDetaljeringFormData';

export const ValgfriDetaljeringForm: FC = () => {
  const navigate = useNavigate();
  const { registrationId } = useParams();
  const { t } = useTranslation();
  const { onSubmit, isLoading, updateError } = useValgfriDetaljeringSubmit();
  const { previousStep } = useRegistrationSteps(RegistrationCommonStep.DETAIILING);
  const { defaultValues, isOversteEtasje, getNextStepFromUserInput } = useDetaljeringFormData();

  const methods = useForm({ defaultValues });

  const navigateBack = () => {
    navigate(`../${previousStep}`);
  };

  // "Valgfri detaljering"-step need response from backend to determine next step
  const onSubmitSuccess = (responseData?: UpdateRegistrationDTO) => {
    const nextStep = getNextStepFromUserInput(responseData?.userInput);

    navigate(`../${nextStep}`);
  };

  const onFormSubmit = (data: DetaljeringForm) => onSubmit({ data, onSuccess: onSubmitSuccess });

  const displayWarning = hasUncheckedDetaljeringSteps(methods.getValues(), methods.formState.dirtyFields);

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={methods.handleSubmit(onFormSubmit)}>
        <Fieldset disabled={isLoading}>
          <FormCard>
            <CheckboxWrapper>
              {/* Bygningsform is different from the other checkboxes. Contains sub selection of vegger and vinduer */}
              <BygningsformCheckBox />

              <DetailingCheckbox name="gulv" />
              {isOversteEtasje && <DetailingCheckbox name="takkonstruksjon" />}
              <DetailingCheckbox name="ytterdorer" />
              <DetailingCheckbox name="lekkasjetall" />
              <DetailingCheckbox name="solceller" />
            </CheckboxWrapper>
          </FormCard>

          {displayWarning && <MightLoseDataWarning />}
          <ErrorBox error={updateError} registrationId={registrationId} />
          <ButtonWrapper>
            <FormBackButton<DetaljeringForm> onSubmit={onSubmit} isLoading={isLoading} onSuccess={navigateBack} />
            <Button loading={isLoading} type="submit">
              {t('form.common.next')} <ChevronRightIcon />
            </Button>
          </ButtonWrapper>
        </Fieldset>
      </form>
    </FormProvider>
  );
};
